import _ from 'lodash'
import {
  fetchIamCustomRoleDefinitionAndCache,
  logout,
  retrySilentSignInWhenFailToRenewFn,
  setAuthValues,
  setMainValues,
  setValues,
  setValuesInternal,
  updateAccessTokenFn,
} from '../../framework/actions'
import { cache, initCache, LIB_TOKENS_KEY, OIDC_SESSION_KEY } from '../../framework/cache'
import {
  AIOPS_APP_ID,
  AIOPS_FREE_APP_ID,
  ASC_PATH_PREFIX,
  ASC_PORTAL_MICRO_APP,
  ASC_TIER_1_SUPPORT_ROLE,
  CN_REGION,
  COMMON_SERVICES_MICRO_APP_NAME,
  COSMOS_URL_KEY,
  DASHBOARDS_MICRO_APP,
  DASHBOARDS_PATH_PREFIX,
  DLP_APP_ID,
  DLP_DEFAULT_ROUTE,
  DLP_MICRO_APP_NAME,
  EMPTY_SAML_DATA,
  FAWKES_APP_ID,
  FAWKES_MGMT_PATH_PREFIX_REGEX,
  FAWKES_MICRO_APP_NAME,
  HUB_URL_KEY,
  MGMT_OVERVIEW_ROUTE,
  MSP_MICRO_APP_NAME,
  MSP_PATH_PREFIX,
  NG_CASB_APP_ID,
  PA_DEFAULT_ROUTE,
  PA_PANORAMA_APP_ID,
  PANW_DOMAIN,
  REQUEST_TIMEOUT,
  RUNNING,
  SAAS_APP_ID,
  SAAS_MICRO_APP_NAME,
  SDWAN_APP_ID,
  TSG_ID_NONE,
  TSG_ID_REGEX,
} from '../../framework/constants'
import { RedirectionViews } from '../../framework/enums'
import { getDatadogRum } from '../../framework/externals'
import { clearLoadingMask } from '../../framework/loadingMask'
import { hasMicroApp } from '../../framework/microApps'
import preference, {
  COMMON_FAVORITES,
  COMMON_LAST_VISITED_TSG_ID,
  COMMON_THEME,
} from '../../framework/preference'
import {
  authed,
  authenticated,
  authorized,
  getAuthState,
  getFrameworkServiceUrl,
  getFrameworkVars,
  getMainState,
  getState,
  history,
  isAppCanBeLaunched,
  loaded,
} from '../../framework/states'
import { stateRef } from '../../framework/store'
import {
  cleanupTsgIdFromUrl,
  fetchIamRolePermissionDefinition,
  fetchTsgInstances,
  fetchTsgs,
  getCurrentTsgId,
  getTsgIdFromUrl,
  getTsgToken,
  getUserFromManager,
  getUserManager,
  hasTsgSupport,
  jwtDecodeCached,
  parseAccess,
  setCurrentTsgId,
  tsgUserManagers,
} from '../../framework/tsg'
import {
  createTimeoutPromise,
  dedupPromise,
  isNonemptyArray,
  setTimeout,
  noop,
  wait,
} from '../../framework/utils'
import { _T } from '../../i18n'
import { __error, __info, __isLocalhost, __warn, _performance } from '../../init/debug'
import { isEmptyObject, tryParseJson } from '../../init/init'
import fetch from '../../libs/fetch'
import { addDataDogTimingOnce, clearOidcSessionStorage } from '../common/utils'
import {
  clearUserSessionForLogout,
  fetchFeatures,
  fetchFqdnInfo,
  fetchLicenseInfo,
  fetchLoggedInUser,
  fetchTokens,
  getFawkesInst,
  getMigrationSatusPromise,
  getNotificationsPromise,
  getSubTenantInfoPromise,
  hasRunningFawkesInst,
  isFawkesAppId,
  isMigrationStateEligibleForFawkesUI,
  isPanoramaManagedTenant,
  registerAGGridLicenseKey,
  registerMapboxToken,
  saveSession,
  unsetSession,
} from './fawkesUtils'
import StartupManager, {
  FAWKES_CONFIG,
  isUserRoleAdemSupport,
  isUserRoleDlpOrDataSecurityAdmin,
  isUserRoleHasManagePermission,
  isUserRoleSaaSAdmin,
  PANORAMA_CONFIG,
} from './StartupManager'

import type { TsgUser, TsgUserManager } from '../../framework/tsg'
import type { Association, ReadonlyTsgInstances } from '../../framework/indexedArrays'
import type {
  Authed,
  MainState,
  AuthState,
  FawkesAuthState,
  AuthInfo,
  SupportAccount,
  UserState,
  Vars,
  OidcState,
  FrameworkVars,
} from '../../framework/stateTypes'

const __timing = _performance.timing

interface SessionError extends Error {
  code?: number
  cause?: any
  logout?: boolean
}

const getOidcSessionData = async (oidc_config_url: string) => {
  const oidcCfgUrl = new URL(oidc_config_url)
  const sessionDataUrl = new URL('/am/json/sessions/?_action=getSessionProperties', oidcCfgUrl.origin).toString()
  const oidcSessionData = await fetch(sessionDataUrl, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Accept-API-Version': 'resource=3.1',
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (res.ok) {
      return res.json().catch((error: Error) => {
        console.error('failed to parse json data for oidc session', error)
        const err = new Error('Failed to parse oidc saml session data') as SessionError
        err.cause = error
        err.logout = true
        throw err
      })
    }
    return res.json().catch(async () => new Error(`get session failed: ${await res.text()}`)).then((error: SessionError) => {
      if (error.code === 401 && !window.location.hostname.endsWith(`.${PANW_DOMAIN}`)) {
        console.error(`Failed to get oidc saml session data probably due to you logged out or 3rd party cookie been blocked, it will also block fetch iam tokens!\nPlease either add "${window.location.hostname}" to the sites that can always use cookies including third-party cookies, or allow all cookies in your browser.`, error)
        if (!__isLocalhost) {
          return EMPTY_SAML_DATA
        }
        console.error('For local development, if you require saml session data, please follow unblock 3rd party cookie, and ensure properly logged in.\nIf you do not require those data, you can turn it off by unset the flag')
      }
      console.error('failed to get oidc saml session data', error)
      const err = new Error('Failed to get oidc saml session data') as SessionError
      err.cause = error
      err.logout = true
      throw err
    })
  }, (err: SessionError) => {
    console.error('failed to get oidc saml session data', err)
    err.logout = true
    throw err
  })
  const { supportaccountids, notonorafter, accesstoken, pingcert } = oidcSessionData || {}
  const isEmptyStr = (str: any) => str === '' || str === '""'
  if (isEmptyStr(supportaccountids) &&
    (isEmptyStr(notonorafter) || notonorafter > 0) &&
    (accesstoken === '""' || accesstoken.length > 10) &&
    (pingcert === '""' || pingcert.length > 10)) {
    // user without any support account
    return { ...EMPTY_SAML_DATA } // clone to bypass use cache check
  }
  if (!supportaccountids || !notonorafter) {
    const err = new Error('Failed to get oidc saml session data, missing expected info') as SessionError
    err.logout = true
    throw err
  }
  let supportAccountIds = []
  if (supportaccountids) {
    try {
      const parsed = JSON.parse(supportaccountids)
      supportAccountIds = Array.isArray(parsed) ? parsed : []
    }
    catch (e) {
      console.error('failed to parse support account ids', supportaccountids)
      const err = new Error('Failed to parse oidc saml session data') as SessionError
      err.cause = e
      err.logout = true
      throw err
    }
  }
  return { supportaccountids, supportAccountIds, notonorafter, accesstoken, pingcert }
}

const loadOidcSessionData = (sub: string) => {
  const vars = getFrameworkVars()
  if (vars.use_provided_tsg_token) {
    return Promise.resolve(EMPTY_SAML_DATA)
  }
  const authority = vars.oidc_client?.authority || vars.oidc_client?.config_url
  const useCached = () => {
    const oidcSession = cache.session.get(OIDC_SESSION_KEY)
    if (oidcSession?.supportAccountIds?.length && oidcSession.sub === sub && oidcSession.notonorafter * 1000 > Date.now() + 600000) { // 10min
      oidcSession.supportaccountids = JSON.stringify(oidcSession.supportAccountIds)
      return oidcSession
    }
    cache.session.delete(OIDC_SESSION_KEY)
  }
  const fn = () => getOidcSessionData(authority!).then((oidcSession) => {
    if (oidcSession === EMPTY_SAML_DATA) {
      oidcSession = useCached() || EMPTY_SAML_DATA
    }
    else {
      const { supportaccountids, ...data } = oidcSession
      cache.session.set(OIDC_SESSION_KEY, Object.assign(data, { sub }))
    }
    return oidcSession
  }, (err) => {
    const oidcSession = useCached()
    if (oidcSession) {
      return oidcSession
    }
    throw err
  })
  return dedupPromise(`oidc-session-${sub}`, fn, { keepPromiseIn: 10000 })
}

const triggerOnAuthed = (resolved: Authed) => {
  const { auth } = resolved
  if (auth) {
    resolved.auth = Object.defineProperties(Object.create(auth), {
      tsg_id: {
        get() {
          return (getAuthState() || auth).tsg_id
        }
      },
      access: {
        get() {
          return (getAuthState() || auth).access
        }
      },
      access_token: {
        get() {
          return (getAuthState() || auth).access_token
        }
      },
      access_token_decoded: {
        get() {
          return (getAuthState() || auth).access_token_decoded
        }
      },
      fawkes_tenant_id: {
        get() {
          return (getAuthState() || auth).fawkes_tenant_id
        }
      },
      __is_init_auth: { value: true },
    })
  }
  return authed.resolve(resolved)
}

const getInitialPreferences = (jwt: string, auth: AuthState, vars: FrameworkVars) => preference.init(jwt).then(() => {
  const theme = preference.get<string>(COMMON_THEME)
  const last_visited_tsg_id = preference.get<string>(COMMON_LAST_VISITED_TSG_ID) || undefined
  const favorites = preference.get(COMMON_FAVORITES) as Record<string, any> | undefined
  const preferences = {} as Record<string, any>
  if (theme && auth.preferences?.user?.theme !== theme) {
    preferences.user = {
      theme,
    }
    sessionStorage.removeItem('spk_user_pref')
    localStorage.spk_user_theme = theme
  }
  if (last_visited_tsg_id && TSG_ID_REGEX.test(last_visited_tsg_id)) {
    preferences.user = {
      ...preferences.user,
      last_visited_tsg_id,
    }
  }
  if (vars.use_favorites) {
    const currTsgId = auth?.tsg_id || ''
    if (currTsgId && favorites?.[currTsgId]) {
      preferences.tsgs = {
        ...stateRef.current?.preferences?.tsgs,
        current: {
          tsg_id: currTsgId,
          favorites: favorites[currTsgId],
        }
      }
    }
  }
  if (!isEmptyObject(preferences)) {
    setValuesInternal({ preferences })
  }
  return preferences
})

const generateAuthInfoForTsg = ({ tsg_id, instances }: { tsg_id: string, instances: ReadonlyTsgInstances }, { notonorafter, supportAccountIds }: { notonorafter: string, supportAccountIds: SupportAccount[] }): AuthInfo | undefined => {
  const hubUrl = getFrameworkServiceUrl(HUB_URL_KEY)
  const vars = getFrameworkVars()
  const fawkesInstances = instances?.filter(inst => inst.status === RUNNING && isFawkesAppId(inst.app_id))
  if (!fawkesInstances?.length) {
    console.warn('no fawkes instance found')
    return
  }
  if (fawkesInstances.length > 1) {
    console.warn('found more than 1 fawkes intances from the tsg', fawkesInstances)
    if (vars.fawkes_session) {
      alert('Multiple Fawkes instances in the same TSG are not supported')
      return
    }
  }
  // make sure fawkes first
  const [cspId, hasMoreCspId] = Array.from(fawkesInstances.concat(instances).reduce((set, inst) => {
    if (isFawkesAppId(inst.app_id) && +inst.support_account_id) {
      set.add(+inst.support_account_id)
    }
    return set
  }, new Set())) as number[]
  if (!cspId) {
    console.warn('no csp id found')
    return
  }
  if (hasMoreCspId) {
    console.warn('found multiple csp ids from tsg instances', tsg_id, cspId, hasMoreCspId)
    alert('Multiple Fawkes instances from different accounts in the same TSG are not supported')
  }
  const existing = new Set(supportAccountIds.map(acc => +acc.accountid))
  const needed = _.uniq(instances.map(t => +t.support_account_id)).filter(id => id && !existing.has(id))
  if (needed.length) { // fake support accounts for token service
    supportAccountIds = supportAccountIds.concat(needed.map(accountid => ({
      accountid,
      name: ' ',
      membershipStatus: 0,
      signature: '==', // faked
      roles: [],
      isFromTsg: true,
    })))
  }
  const rbacRoles = instances.reduce((o, inst) => {
    const app = o[inst.app_id] || (o[inst.app_id] = {})
    app[inst.tenant_id] = { 'Instance Admin': true }
    return o
  }, {} as Record<string, any>)
  let hasAccount = false
  if (supportAccountIds?.length && cspId > 0) {
    for (const acc of supportAccountIds) {
      if (+acc.accountid === cspId) {
        hasAccount = true
        acc.rbacRoles = rbacRoles
      }
      else {
        acc.rbacRoles = {}
      }
    }
  }
  if (!hasAccount) {
    console.info('allow load fawkes without the csp account access', cspId)
  }
  const entitlements = instances.reduce((ent, inst) => {
    const app = ent[inst.app_id] || (ent[inst.app_id] = {
      app_id: inst.app_id,
      enabled: true,
      entitled: true,
      tenants: []
    })
    const {
      tenant_id,
      associations,
      provisioning_status,
      status: instance_status = provisioning_status,
      license_expiration: expiration,
      region,
      serial_number,
      entitlements,
      url,
    } = inst
    app.tenants.push({
      tenant_id,
      associations: isNonemptyArray(associations) ? associations!.reduce((o, asso) => {
        o[asso.app_id] = asso
        return o
      }, {} as Record<string, Association>) : {},
      instance_status,
      expiration,
      region,
      serial_number,
      entitlements,
      url,
    })
    return ent
  }, {
    'App Portal': {
      support_account_id: cspId,
      rbac_roles: rbacRoles,
      tsg_id,
      url: hubUrl,
    },
  } as Record<string, any>)
  const appPortalUrl = new URL('/hub', hubUrl)
  appPortalUrl.searchParams.set('tsg_id', tsg_id)
  const authInfo = {
    credentials: {
      notonorafter,
      supportAccountIds,
    },
    entitlements,
    extraInfo: {
      appPortal: appPortalUrl.toString(),
    }
  } as AuthInfo
  __info('generated authInfo', authInfo)
  return authInfo
}

const infoLoadedWithoutFawkes = async (auth: AuthState, tsgAuthInfo?: AuthInfo) => {
  const main = { ...stateRef.current.main, isLoggedIn: true } as MainState
  const {
    email,
    // username: userName,
    firstname,
    lastname,
    supportaccountids = '[]',
    supportAccountIds = [],
    notonorafter,
    accesstoken,
    pingcert,
  } = auth.user!
  const { private_site_origin, remove_main_state } = getFrameworkVars()
  const tenant = private_site_origin ? undefined : {
    type: FAWKES_CONFIG,
    info: {},
  } as any
  const fawkesInst = getFawkesInst(auth.instances)
  if (fawkesInst) {
    const {
      tenant_id: tenantId,
      support_account_id: supportAccountId,
      support_account_name: accountName,
      tsg_id: sgId,
      iam_controlled: iamControlled,
      state,
      runtime_attributes: {
        paas_api_url: fawkes_paas_api_url,
        admin_api_url: fawkes_admin_api_url,
      } = {},
    } = fawkesInst
    Object.assign(tenant, {
      id: tenantId,
      tenantId,
      sgId,
      supportAccountId,
      accountName,
      info: fawkesInst,
      type: isPanoramaManagedTenant(auth),
      state,
      iamControlled,
    })
    Object.assign(auth, {
      fawkes_tenant_id: tenantId,
      fawkes_paas_api_url,
      fawkes_admin_api_url,
    })
    if (fawkes_admin_api_url) {
      window.__admin_cluster_url = fawkes_admin_api_url // update global just in case
    }
  }
  const res = private_site_origin ? undefined : auth.access?.find(scope => scope.app_id === FAWKES_APP_ID) || auth.access?.find(scope => !scope.app_id)
  const roles = res?.aggRoleNames || res?.roleNames || []
  main.loggedInUser = {
    email,
    userName: email,
    supportaccountids,
    supportAccountIds,
    profile: {
      email,
      firstname,
      lastname,
      displayName: '',
      accesstoken,
      groups: '""',
      cert: '',
      supportaccountids,
      notonorafter,
      pingcert,
      // 'block': '0',
    },
    tenant,
    roles,
    preference: {},
    extra: {},
  }
  main.tenant = tenant
  main.authInfo = tsgAuthInfo
  const region = auth.instances?.getPlatformRegion?.()
  const cosmosUrl = region && auth.fawkes_region_wo_api && getFrameworkServiceUrl(COSMOS_URL_KEY, { region })
  if (cosmosUrl) {
    main.featuresInfo = [{ name: 'pai', toggle: 1 }]
    main.fqdnInfo = {
      msg: {
        paifqdn: new URL(cosmosUrl).hostname,
        http_code: 200,
        mocked: true,
      }
    }
  }

  const promises: Promise<any>[] = []
  const waitExtraPromises = (...args: any[]): Promise<any[]> => {
    const addPromises = (args.flat?.(1) || _.flatten(args)).filter(p => p.then)
    if (addPromises.length) {
      const [timeoutPromise, clear] = createTimeoutPromise(15000, 'waitExtraPromises 15s timeout')
      const promise = Promise.race([
        Promise.all(addPromises).then((res) => {
          clear()
          return res
        }),
        timeoutPromise.catch(() => [] as any[]),
      ])
      promises.push(promise)
      return promise
    }
    return Promise.resolve([])
  }
  const resolvedMain = Object.defineProperties(Object.create(main), { main: { value: main } })
  const resolvedMainFn = () => Promise.resolve(resolvedMain)
  const resolved = {
    main,
    auth,
    loaded,
    getFeatures: resolvedMainFn,
    getFqdnInfo: resolvedMainFn,
    getLicenseInfo: resolvedMainFn,
    getSubTenantInfo: resolvedMainFn,
    getAuthInfo: resolvedMainFn,
    getNotifications: resolvedMainFn,
    getMigrationStatus: resolvedMainFn,
    getTokenManager: undefined,
    waitExtraPromises,
    StartupManager,
    getAuthState,
    getMainState,
    getState,
    setAuthValues,
    setMainValues,
    setValues,
  }
  if (!auth.apps && stateRef.current.auth?.apps?.length) {
    auth.apps = stateRef.current.auth?.apps
  }
  if (remove_main_state) {
    const newMain = _.pick(main, ['featuresInfo', 'isLoggedIn', 'infoLoaded', 'configLocation'])
    setValuesInternal({ auth, main: newMain })
  }
  else {
    setValuesInternal({ main, auth })
  }
  triggerOnAuthed(resolved) // ensure trigger onAuthed even without fawkes
  await authed.catch(noop) // ignore rejection
  if (promises.length) {
    await Promise.allSettled(promises)
  }

  main.infoLoaded = true
  setMainValues({ infoLoaded: true })

  loaded.resolve(main)
}

export const loadFawkesMainInfo = async (tsgAuthInfo: AuthInfo) => {
  const starts = Date.now()
  __timing.fawkes_main_info_load_start = starts
  const { auth, main: mainState, leftMainNavigationPromise } = stateRef.current
  const main = { ...mainState, isLoggedIn: true } as MainState
  const vars = getFrameworkVars()
  const resolvedMain = Object.defineProperties(Object.create(main), { main: { value: main } })
  const methodPromises = {} as Record<string, Promise<MainState> | undefined>
  const promises: Promise<any>[] = []
  const critialErrors = [] as Error[]
  const collected = {
    cacheUsed: false,
    hasError: false,
  }
  const abortController = new AbortController()
  const fetchPromiseOption = { signal: abortController.signal, tsg_id: auth.tsg_id }
  const fawkesWithoutPA = hasTsgSupport() && auth?.instances && (
    auth.instances.get(FAWKES_APP_ID)?.runtime_attributes?.hidden ||
    !auth.instances.some(inst => inst.app_id.startsWith(FAWKES_APP_ID))
  )
  let tenantId: string | undefined = (vars.use_cached_main_info &&
    cache.session.get(`fawkes-user-${auth.tsg_id}`)?.tenant?.tenantId) || undefined
  const getTokenInfo = (): Promise<MainState> => {
    if (methodPromises.tokenInfo) {
      return methodPromises.tokenInfo
    }
    methodPromises.tokenInfo = cache.get(LIB_TOKENS_KEY).catch(noop).then((cachedToken) => {
      if (cachedToken && cachedToken.cached > Date.now() - 900000) { // 15min ago
        collected.cacheUsed = true
        return cachedToken
      }
      const promise = fetchTokens().then((tokenInfo) => {
        cache.set(LIB_TOKENS_KEY, Object.assign(tokenInfo, { cached: Date.now() }))
        return tokenInfo
      }, (err: Error) => {
        console.error('failed to get tokens', err, err.cause)
      })
      if (cachedToken) {
        return cachedToken
      }
      return promise
    }).then((tokenInfo) => {
      main.tokenInfo = tokenInfo
      if (tokenInfo) {
        const { mapBox, agGrid } = tokenInfo
        if (mapBox) {
          registerMapboxToken(mapBox)
        }
        if (agGrid) {
          registerAGGridLicenseKey(agGrid)
        }
        if (!stateRef.current.libTokens) {
          setValuesInternal({
            libTokens: { mapBox, agGrid },
          })
        }
      }
      return resolvedMain
    }, (err: Error) => {
      collected.hasError = true
      console.error('failed to get tokens', err)
    }) as Promise<MainState>
    promises.push(methodPromises.tokenInfo)
    return methodPromises.tokenInfo
  }
  const getFqdnInfo = (): Promise<MainState> => {
    if (methodPromises.fqdnInfo) {
      return methodPromises.fqdnInfo
    }
    const cacheKey = `gp-fqdn-${tenantId}`
    const fetchFqdn = () => fetchFqdnInfo(fetchPromiseOption).then((fqdnInfo) => {
      if (fqdnInfo && !abortController.signal.aborted) {
        main.fqdnInfo = fqdnInfo
      }
      if (main.fqdnInfo) {
        cache.session.set(cacheKey, main.fqdnInfo)
      }
      return resolvedMain
    }, (err: Error) => {
      if (abortController.signal.aborted) {
        return resolvedMain
      }
      if (fawkesWithoutPA) {
        console.info('failed to get fqdn info may due to no pa license ready yet, use fallback cosmos fqdn instead', err)
        const region = auth?.instances?.getPlatformRegion?.()
        const cosmosUrl = region && getFrameworkServiceUrl(COSMOS_URL_KEY, { region })
        if (cosmosUrl) {
          main.fqdnInfo = {
            msg: {
              paifqdn: new URL(cosmosUrl).hostname,
              http_code: 200,
              mocked: true,
            }
          }
        }
        return resolvedMain
      }
      collected.hasError = true
      if (cached) {
        main.licenseInfo = cached
        collected.cacheUsed = true
        console.error('failed to get fqdn use cache instead', err, err.cause)
        return resolvedMain
      }
      const error = new Error('Failed to fetch required tenant data, please try reload.')
      error.cause = err
      critialErrors.push(error)
      console.error('failed to get fqdn', err)
      // throw err
    }) as Promise<MainState>
    const cached = cache.session.get(cacheKey)
    if (cached && vars.use_cached_main_info) {
      main.fqdnInfo = cached
      collected.cacheUsed = true
      methodPromises.fqdnInfo = Promise.resolve(resolvedMain)
      fetchFqdn()
    }
    else {
      methodPromises.fqdnInfo = fetchFqdn()
      promises.push(methodPromises.fqdnInfo)
    }
    return methodPromises.fqdnInfo
  }
  const getFeatures = (): Promise<MainState> => {
    if (methodPromises.featuresInfo) {
      return methodPromises.featuresInfo
    }
    const cacheKey = `features-${tenantId}`
    const fetchFeaturesFn = () => fetchFeatures(fetchPromiseOption).then((features) => {
      if (!abortController.signal.aborted) {
        main.featuresInfo = features
      }
      if (tenantId && isNonemptyArray(features)) {
        cache.session.set(cacheKey, features)
      }
      return resolvedMain
    }, (err: Error) => {
      if (abortController.signal.aborted) {
        return resolvedMain
      }
      console.error('failed to get features', err, err.cause)
      collected.hasError = true
      if (cached) {
        main.featuresInfo = cached
        collected.cacheUsed = true
        return resolvedMain
      }
      // throw err
    }) as Promise<MainState>
    const cached = tenantId ? cache.session.get<MainState['featuresInfo']>(cacheKey) : undefined
    if (cached && vars.use_cached_main_info) {
      main.featuresInfo = cached
      collected.cacheUsed = true
      methodPromises.featuresInfo = Promise.resolve(resolvedMain)
      fetchFeaturesFn() // TODO: may need to check and update state
    }
    else {
      methodPromises.featuresInfo = fetchFeaturesFn()
      promises.push(methodPromises.featuresInfo)
    }
    return methodPromises.featuresInfo
  }
  const getLicenseInfo = (): Promise<MainState> => {
    if (methodPromises.licenseInfo) {
      return methodPromises.licenseInfo
    }
    const cacheKey = `license-info-${tenantId}`
    const fetchLicenseInfoFn = () => fetchLicenseInfo(fetchPromiseOption).then((licenseInfo) => {
      if (!abortController.signal.aborted) {
        main.licenseInfo = licenseInfo
      }
      if (tenantId && !_.isEmpty(main.licenseInfo)) {
        cache.session.set(cacheKey, main.licenseInfo)
      }
      return resolvedMain
    }, (err: Error) => {
      if (abortController.signal.aborted) {
        return resolvedMain
      }
      if (fawkesWithoutPA) {
        console.info('failed to get license info may due to no pa license ready yet', err)
        return resolvedMain
      }
      collected.hasError = true
      console.error('failed to get license info', err, err.cause)
      if (cached) {
        main.licenseInfo = cached
        collected.cacheUsed = true
        return resolvedMain
      }
      // throw err
    }) as Promise<MainState>
    const cached = tenantId ? cache.session.get<MainState['licenseInfo']>(cacheKey) : undefined
    if (cached && vars.use_cached_main_info) {
      main.licenseInfo = cached
      collected.cacheUsed = true
      fetchLicenseInfoFn() // TODO: may need to check and update state
      methodPromises.licenseInfo = Promise.resolve(resolvedMain)
    }
    else {
      methodPromises.licenseInfo = fetchLicenseInfoFn()
      promises.push(methodPromises.licenseInfo)
    }
    return methodPromises.licenseInfo
  }

  const loadLoggedInUser = () => {
    if (!vars.use_cached_main_info) {
      return fetchLoggedInUser(auth.tsg_id)
    }
    const fetchLoggedInUserFn = () => (
      fetchLoggedInUser(auth.tsg_id).then((user) => {
        cache.session.set(cacheKey, user)
        return user
      })
    )
    const cacheKey = `fawkes-user-${auth.tsg_id}`
    const cached = cache.session.get(cacheKey)
    if (cached && vars.use_cached_main_info) {
      collected.cacheUsed = true
      fetchLoggedInUserFn() // TODO: may need to check and update state
      return Promise.resolve(cached)
    }
    return fetchLoggedInUserFn()
  }

  const userPromise = loadLoggedInUser().catch((e) => {
    console.error('failed to get logged in user', e, e.cause)
    if (e.cause?.errorMessage) {
      e.errorCode = e.cause.errorCode
      if (e.errorCode?.startsWith?.('prisma access tenant not found')) {
        e.errorMessage = _T('Prisma Access or management is not running in this tenant.')
      }
      else {
        e.errorMessage = e.cause.errorMessage
      }
    }
    throw e
  }) as Promise<{ user?: any, tenant?: any, preference?: any, extra?: any, hasCsrfSalt?: boolean, csrfSalt?: string, error?: string }>

  getTokenInfo()
  if (tenantId && vars.auto_load_info !== false) {
    getFqdnInfo()
    getFeatures()
    getLicenseInfo()
  }

  const user = await userPromise
  if (!user?.user) {
    abortController.abort()
    clearLoadingMask()
    if (user.error) {
      setValuesInternal({
        loginFailed: {
          message: user.error,
        }
      })
    }
    return
  }
  const {
    // accesstoken,
    email,
    firstname,
    lastname,
    // username: userName,
    supportaccountids = '[]',
    supportAccountIds = [],
    notonorafter,
    accesstoken,
    pingcert,
  } = auth?.user || {}
  main.loggedInUser = {
    supportaccountids,
    supportAccountIds,
    userName: email,
    ...user.user,
    profile: {
      email,
      firstname,
      lastname,
      displayName: '',
      accesstoken,
      groups: '""',
      cert: '',
      supportaccountids,
      notonorafter,
      pingcert,
      ...user.user.profile,
      // 'block': '0',
    },
    tenant: user.tenant,
    preference: user.preference?.params || {},
    extra: user.extra,
  }
  main.tenant = user.tenant
  main.authInfo = tsgAuthInfo
  if (!main.loggedInUser!.supportAccountIds?.length) {
    const cspStr = user.user?.profile?.supportaccountids || supportaccountids
    if (cspStr?.length > 2) {
      main.loggedInUser!.supportAccountIds = tryParseJson(cspStr)
    }
  }
  tenantId = main.tenant?.tenantId as string | undefined
  const cachedFqdnInfo = cache.session.get(`gp-fqdn-${tenantId}`)
  if (cachedFqdnInfo) {
    main.fqdnInfo = cachedFqdnInfo
  }

  const resolved: Partial<Authed> = { main, auth, loaded }
  if (leftMainNavigationPromise) { // wait for micro-app to be loaded
    promises.push(leftMainNavigationPromise.catch(noop))
  }
  const getNotifications = (): Promise<MainState> => {
    if (vars.skip_extra_fawkes_fetch) {
      __info('skip get notifications from fawkes')
      main.notificationsInfo = []
      return resolvedMain
    }
    if (methodPromises.notification) {
      return methodPromises.notification
    }
    methodPromises.notification = getNotificationsPromise().then((notifications) => {
      main.notificationsInfo = notifications?.response
      return resolvedMain
    }, (err: Error) => {
      collected.hasError = true
      console.error('failed to get notifications info', err)
      // throw err
    }) as Promise<MainState>
    promises.push(methodPromises.notification)
    return methodPromises.notification
  }
  const getSubTenantInfo = (): Promise<MainState> => {
    if (vars.skip_extra_fawkes_fetch) {
      __info('skip get sub tenant from fawkes')
      return resolvedMain
    }
    if (methodPromises.subTenantInfo) {
      return methodPromises.subTenantInfo
    }
    methodPromises.subTenantInfo = getSubTenantInfoPromise().then((subTenant) => {
      main.subTenantInfo = subTenant?.response
      const itemsStr = main.subTenantInfo?.msg?.items
      const items = itemsStr && tryParseJson(itemsStr)
      main.subTenantInfo.isMultiTenancy = items?.length > 1
      if (itemsStr && !Array.isArray(items)) {
        collected.hasError = true
        console.error('failed to parse sub tenant info', itemsStr)
      }
      return resolvedMain
    }, (err: Error) => {
      collected.hasError = true
      console.error('failed to get sub tenant info', err)
      // throw err
    }) as Promise<MainState>
    promises.push(methodPromises.subTenantInfo)
    return methodPromises.subTenantInfo
  }
  const getMigrationStatus = (): Promise<MainState> => {
    if (vars.skip_extra_fawkes_fetch) {
      __info('skip get migration status from fawkes')
      return resolvedMain
    }
    if (methodPromises.migrationStatus) {
      return methodPromises.migrationStatus
    }
    const cacheKey = `fawkes-migration-status-${tenantId}`
    const fetchMigrationStatus = () => getMigrationSatusPromise().then((migrateStatus) => {
      const {
        state,
        fawkesTenantId,
      } = migrateStatus?.response || {}
      main.migrationStatus = state
      main.migrationNewFawkesTenantId = fawkesTenantId
      if (!state && !fawkesTenantId) { // only cache if no migration status
        cache.session.set(cacheKey, { state })
      }
      else {
        cache.session.delete(cacheKey)
      }
      return resolvedMain
    }, (e) => {
      collected.hasError = true
      console.error('failed to get migration status', e)
    }) as Promise<MainState>
    const cached = cache.session.get(cacheKey)
    if (cached && vars.use_cached_main_info) {
      main.migrationStatus = ''
      collected.cacheUsed = true
      methodPromises.migrationStatus = Promise.resolve(resolvedMain)
      fetchMigrationStatus()
    }
    else {
      methodPromises.migrationStatus = fetchMigrationStatus()
      promises.push(methodPromises.migrationStatus)
    }
    return methodPromises.migrationStatus
  }

  const getAuthInfo = (): Promise<MainState> => resolvedMain
  const waitExtraPromises = (...args: any[]): Promise<any[]> => {
    const addPromises = (args.flat?.(1) || _.flatten(args)).filter(p => p.then)
    if (addPromises.length) {
      const [timeoutPromise, clear] = createTimeoutPromise(15000, 'waitExtraPromises 15s timeout')
      const promise = Promise.race([
        Promise.all(addPromises).then((res) => {
          clear()
          return res
        }),
        timeoutPromise.catch(() => [] as any[]),
      ])
      promises.push(promise)
      return promise
    }
    return Promise.resolve([])
  }
  Object.assign(resolved, {
    getFeatures,
    getFqdnInfo,
    getLicenseInfo,
    getSubTenantInfo,
    getAuthInfo,
    getNotifications,
    getMigrationStatus,
    getTokenManager: undefined,
    waitExtraPromises,
    StartupManager,
    getAuthState,
    getMainState,
    getState,
    setAuthValues,
    setMainValues,
    setValues,
  })
  if (!vars?.remove_main_state) {
    setMainValues(main)
  }
  triggerOnAuthed(resolved as Authed)
  await authed.catch(noop) // ensure other micro-app wait for authed executed

  // defaults
  getTokenInfo()
  if (vars.auto_load_info !== false) {
    getFeatures()
    getFqdnInfo()
    getLicenseInfo()
  }

  if (promises.length) {
    let ts: number
    const timeoutPromise = new Promise((resolve) => {
      ts = setTimeout(() => {
        resolve({
          error: new Error('Authentication timeout occurred. Please try again.')
        })
      }, 30000) // 30s
    })
    const res: { error?: Error } = await Promise.race<{}>([
      timeoutPromise,
      Promise.allSettled(promises).then(() => {
        clearTimeout(ts)
        return {}
      }),
    ])
    const error: Error | undefined = res.error ||
      (!hasTsgSupport() && critialErrors[0]) || undefined
    if (error) {
      clearLoadingMask()
      setValuesInternal({
        loginFailed: {
          message: error.message,
          reload: true,
        }
      })
      return
    }
  }

  if (!main.featuresInfo) {
    main.featuresInfo = []
  }

  main.infoLoaded = true
  if (!vars?.remove_main_state) {
    setValuesInternal({ main })
  }
  else {
    const newMain = _.pick(main, ['featuresInfo', 'isLoggedIn', 'infoLoaded', 'configLocation'])
    setValuesInternal({ main: newMain })
  }

  const end = Date.now()
  const duration = end - starts
  let __log = __info
  if (duration > 25000 || collected.hasError) {
    __log = __error
  }
  else if (duration > 5000) {
    __log = __warn
  }
  __timing.fawkes_main_info_load_duration = duration
  __timing.fawkes_main_info_load_end = end
  __log(`fawkes main info loaded in ${duration}ms ${collected.cacheUsed ? 'with cache used' : 'without cache'}`, main)
  addDataDogTimingOnce('fawkes_main_info_loaded', end)
  loaded.resolve(main)
}

export let syncLocationOnce = () => {
  if (syncLocationOnce !== noop &&
    window.location.pathname !== history.location.pathname &&
    history.location.pathname.startsWith('/authentication/callback')) {
    __info('sync location', window.location.pathname)
    history.replace(window.location.pathname)
    syncLocationOnce = noop // ensure once
  }
}

const handleIamFawkesSession = async (auth: AuthState) => {
  const {
    access_token_decoded,
    instances,
  } = auth
  const tsg_id = auth.tsg_id!
  const access_token = auth.access_token!
  unsetSession() // ensure previous tenant id been cleared
  const { supportaccountids, supportAccountIds, notonorafter, accesstoken, pingcert, email, subname = email } = auth.user as UserState
  const sessionData = supportAccountIds && notonorafter ? {
    supportaccountids,
    supportAccountIds,
    notonorafter,
    accesstoken,
    pingcert,
  } : await loadOidcSessionData(subname as string).catch(() => EMPTY_SAML_DATA)
  const tsgAuthInfo = generateAuthInfoForTsg({ tsg_id: tsg_id!, instances }, sessionData)
  if (!tsgAuthInfo) {
    throw new Error('failed to generate auth info for tsg')
  }
  // always stateless mode, and already checked running status
  const fawkesInst = getFawkesInst(instances)! // isRunningStatus checked
  const {
    tenant_id: fawkes_tenant_id,
    runtime_attributes: {
      paas_api_url: fawkes_paas_api_url,
      admin_api_url: fawkes_admin_api_url,
    } = {}
  } = fawkesInst
  const fawkes_region_wo_api = instances.getPlatformRegion() === CN_REGION && !fawkesInst.runtime_attributes?.admin_api_url
  if (!fawkes_tenant_id || !(fawkes_paas_api_url || fawkes_region_wo_api)) {
    console.error('not able to get valid fawkes instance, cannot find tenant_id or api_url', fawkesInst)
    throw new Error('Failed to initialize Prisma Access management due to missing critical info')
  }
  const fawkesInstanceInfo = {
    fawkes_tenant_id,
    fawkes_paas_api_url,
    fawkes_admin_api_url,
    fawkes_region_wo_api,
  } as FawkesAuthState
  if (fawkes_admin_api_url) {
    window.__admin_cluster_url = fawkes_admin_api_url // update global just in case
  }

  const exp = access_token_decoded?.exp || jwtDecodeCached(access_token, tsg_id)?.exp
  const fawkesSession = {
    sub: fawkesInstanceInfo.fawkes_tenant_id,
    token: access_token,
    exp,
  }
  __warn('save fawkes session', fawkesSession)
  saveSession(fawkesSession)

  await setAuthValues(Object.assign(auth, { // use await for delay loadMainInfo
    user: {
      ...auth.user,
      ...sessionData,
    },
    ...fawkesInstanceInfo,
  }))

  // wait for the setAuthValues above applied
  let counter = 300 // 30s
  while (stateRef.current.auth?.access_token !== access_token && --counter) {
    __info('access token not synced, waiting for sync')
    await wait(100)
  }
  if (counter <= 0) {
    __error('access token is not able to sync to global store auth state:', stateRef.current.auth, auth)
    alert('Initialize user session failed, try reload the browser.')
    window.location.reload()
    return
  }

  __info('auth', auth)
  if (fawkes_region_wo_api && !fawkesInst.runtime_attributes) {
    // load without fawkes:
    await infoLoadedWithoutFawkes(auth, tsgAuthInfo)
  }
  else {
    await loadFawkesMainInfo(tsgAuthInfo)
  }
  syncLocationOnce()
}

type Oidc = {
  oidcUser: TsgUser,
  userManager: TsgUserManager,
  login: () => Promise<void>
}
export const handleOidcLogin = async (oidc: Oidc) => {
  const starts = Date.now()
  __timing.tsg_tenant_auth_load_start = starts
  let tsg_id: string
  const { oidcUser, userManager, login } = oidc
  const {
    access_token,
    id_token,
    profile: {
      sub: subname,
      email,
      name: userName,
      given_name: firstname,
      family_name: lastname,
    } = {}
  } = oidcUser
  if (subname) {
    try {
      getDatadogRum()?.setUser?.({ id: subname })
    }
    catch (e) {
      __error('failed to set datadog user id', e)
    }
    initCache({ user_id: subname })
  }
  const vars = getFrameworkVars()
  const decodedLoginJwt = jwtDecodeCached(access_token)
  const handleAuthorized = async (auth: AuthState) => {
    await loaded
    authorized.resolve(auth)
    await authorized
  }

  const auth = {
    ...stateRef.current.auth,
    access_token,
    access_token_decoded: decodedLoginJwt,
    loading: false,
    id_token,
    user: {
      subname,
      email,
      username: userName,
      firstname,
      lastname,
    },
  } as AuthState
  const ftPrivateSiteTsgSupport = Boolean(vars.private_site_origin && vars.private_site_tsg_support && decodedLoginJwt.tsg_id)
  userManager.events.addUserUnloaded(clearUserSessionForLogout)
  userManager.events.addUserLoaded((tsgUser) => {
    if (stateRef.current.auth?.id_token !== tsgUser.id_token) {
      const update = { // update id token
        id_token: tsgUser.id_token,
      } as AuthState
      if (vars.private_site_origin && !ftPrivateSiteTsgSupport) { // update acces_token for private site only
        update.access_token = tsgUser.access_token
        update.access_token_decoded = jwtDecodeCached(tsgUser.access_token)
      }
      setAuthValues(update)
    }
  })
  if (decodedLoginJwt.tsg_id) {
    userManager.tsg_id = decodedLoginJwt.tsg_id
    userManager.events.addSilentRenewError(() => {
      getUserFromManager(userManager, {
        forceSignIn: true,
        tsg_id: decodedLoginJwt.tsg_id,
      }).catch((err) => {
        console.warn('failed to fix renew user session', err)
      })
    })
  }

  authenticated.resolve(auth)

  if (vars.private_site_origin) {
    if (window.location.origin !== vars.private_site_origin) {
      return setValuesInternal({
        loginFailed: {
          message: 'Private site origin is not matched',
        }
      })
    }
    if (!ftPrivateSiteTsgSupport) {
      // load without fawkes if w/o tsg support
      await infoLoadedWithoutFawkes(auth)

      authorized.resolve(auth)
      return syncLocationOnce()
    }
  }

  if (!decodedLoginJwt?.tsg_id) {
    console.error('cannot detect tenant info in the token', decodedLoginJwt, oidcUser)
    return setValuesInternal({
      loginFailed: {
        message: 'cannot detect tenant info in the token',
      }
    })
  }
  if (userManager) {
    tsgUserManagers[''] = userManager // set login user manager
  }
  else {
    return setValuesInternal({
      loginFailed: {
        message: 'not able to find default user manager from oidc',
      }
    })
  }

  let defaultTsgToken: string
  if (decodedLoginJwt.tsg_id === TSG_ID_NONE) {
    defaultTsgToken = access_token
    tsgUserManagers[TSG_ID_NONE] = userManager
  }
  else { // get tsg id none
    defaultTsgToken = await getTsgToken(TSG_ID_NONE).catch(() => access_token)
  }

  const preferencePromise = vars.use_user_preferences ? getInitialPreferences(defaultTsgToken, auth, vars) : undefined

  if (vars.load_oidc_session || hasMicroApp(COMMON_SERVICES_MICRO_APP_NAME)) {
    auth.sessionDataPromise = loadOidcSessionData(subname || email || '').catch((err) => {
      const RETRY_MSG = 'Failed to get SAML session. Do you want to reload the browser to try again?\nOtherwise continue without Support Account access which will not able to onboard services.'
      if (window.confirm(RETRY_MSG)) {
        const relogin = () => {
          setCurrentTsgId(tsg_id)
          login()
          return err
        }
        console.error('force re-login due to failed to get user SAML session data', err)
        userManager.events.removeUserUnloaded(clearUserSessionForLogout)
        return userManager.removeUser().then(relogin, relogin) // will clear session
      }
      // ignore other errors
    })
  }

  const metadataPromise = !userManager.settings.metadata &&
    (userManager.settings as any).metadataService?.getMetadata()
  const tsgsPromise = fetchTsgs(defaultTsgToken, email)
  const rolePermissionsPromise = fetchIamRolePermissionDefinition(defaultTsgToken)

  const tsgFromUrl = getTsgIdFromUrl()
  tsg_id = tsgFromUrl || getCurrentTsgId() ||
    (preferencePromise && (await preferencePromise)?.user?.last_visited_tsg_id) ||
    (decodedLoginJwt.tsg_id !== TSG_ID_NONE && decodedLoginJwt.tsg_id) || ''
  if (tsg_id !== decodedLoginJwt.tsg_id) { // not matched, try silent login to different tsg
    if (decodedLoginJwt.tsg_id === TSG_ID_NONE) {
      tsg_id = TSG_ID_NONE // set to none
    }
    else {
      try {
        await metadataPromise
        auth.access_token = await getTsgToken(tsg_id)
        auth.access_token_decoded = jwtDecodeCached(auth.access_token, tsg_id)
      }
      catch (err) {
        console.error('failed to get access_token for current tsg_id', tsg_id, err)
        tsg_id = decodedLoginJwt.tsg_id // revert to default tsg_id
        if (tsgFromUrl && tsg_id !== tsgFromUrl) {
          alert(`You do not have access to the specified tenant with TSG ID ${tsgFromUrl}, fallback to load TSG ID ${tsg_id} instead.`)
        }
      }
    }
  }
  auth.tsg_id = tsg_id
  await metadataPromise
  const tsgMgr = getUserManager(tsg_id)
  tsgMgr.events.addUserLoaded(updateAccessTokenFn(tsg_id))
  tsgMgr.events.addSilentRenewError(retrySilentSignInWhenFailToRenewFn(tsgMgr))

  setCurrentTsgId(tsg_id)
  try {
    getDatadogRum()?.setGlobalContextProperty?.('global_tsg_id', tsg_id)
  }
  catch (e) {
    __error('failed to set datadog context global_tsg_id', e)
  }

  const customRolesPromise = fetchIamCustomRoleDefinitionAndCache(tsg_id).catch((err) => {
    console.error('failed to fetch custom roles', err)
    return undefined
  })
  auth.instances = await fetchTsgInstances(tsg_id).catch((err) => {
    setValuesInternal({
      loginFailed: {
        message: err.message,
      }
    })
    return err.instances
  })

  const fawkesSessionPromise = vars.fawkes_session && hasRunningFawkesInst(auth.instances) ?
    handleIamFawkesSession(auth).then(() => true, (err) => err || false) : false

  auth.rolePermissionDefinition = await rolePermissionsPromise
  auth.rolePermissionDefinition.customRoles = await customRolesPromise
  auth.access = parseAccess(
    auth.access_token_decoded!.access,
    auth.rolePermissionDefinition,
  )
  if (tsgFromUrl) { // remove tsg_id from url for now, before we fully support persistent query
    cleanupTsgIdFromUrl()
  }

  auth.tsgs = await tsgsPromise
  const tsg_name = tsg_id && auth.tsgs.get(tsg_id)?.display_name
  if (tsg_name) {
    auth.tsg_name = tsg_name
  }
  else if (!auth.tsgs.has(tsg_id)) {
    console.error(`cannot find the tsg ${tsg_id} from the tsg tree`)
  }
  // console.error('tsgs', auth.tsgs)
  if (auth.sessionDataPromise) {
    const sessionData = await auth.sessionDataPromise
    if (sessionData?.logout) { // force relogin handled in promise
      return
    }
    Object.assign(auth.user!, sessionData)
    delete auth.sessionDataPromise
  }
  setAuthValues(auth) // ensure save tsgs and oidc session

  if (preferencePromise) {
    await preferencePromise
    if (preference.get(COMMON_LAST_VISITED_TSG_ID) !== tsg_id && tsg_id !== TSG_ID_NONE && tsg_name) {
      preference.set(COMMON_LAST_VISITED_TSG_ID, tsg_id, { token: defaultTsgToken }).catch((err) => {
        console.error('failed to update last visited tsg_id', tsg_id, err)
      })
    }
  }

  __timing.tsg_tenant_auth_load_end = Date.now()
  __timing.tsg_tenant_auth_load_duration = __timing.tsg_tenant_auth_load_end - starts
  __info(`tsg tenant, auth and preference loaded in ${__timing.tsg_tenant_auth_load_duration}ms`)
  addDataDogTimingOnce('tsg_tenant_auth_loaded', __timing.tsg_tenant_auth_load_end)

  if (fawkesSessionPromise) {
    const result = await fawkesSessionPromise
    if (result === true) {
      handleAuthorized(auth)
      return // handlded by loadMainInfo
    } // if failed, fallback to use below without fawkes
    else {
      if (result.logout) {
        if (result.message) {
          clearLoadingMask()
          setValuesInternal({
            loginFailed: {
              message: result.message,
            }
          })
        }
        else {
          logout()
        }
        return
      }
      console.warn('failed to init fawkes session', result)
      let path = window.location.pathname || '/' // do not use history.location which out of sync
      if (path === '/' && vars.default_route) {
        path = vars.default_route
      }
      const fawkesPath = FAWKES_MGMT_PATH_PREFIX_REGEX.test(path)
      if (fawkesPath && !vars?.remove_main_state) {
        const errMsg = result.errorMessage || result.message
        const message = errMsg && /^[A-Z]/.test(errMsg) ? errMsg : `Failed to initialize${errMsg ? ` due to ${errMsg}` : ''}`
        setMainValues({
          fawkesSessionError: {
            from: window.location.href,
            message,
            msp: hasMicroApp(MSP_MICRO_APP_NAME) && auth.tsg_id && auth.tsgs.hasSubTenants(auth.tsg_id) ?
              vars.msp_route || MSP_PATH_PREFIX : undefined,
          }
        })
        history.push(PA_DEFAULT_ROUTE)
      }
    }
  }

  // load without fawkes:
  await infoLoadedWithoutFawkes(auth)

  handleAuthorized(auth)
  return syncLocationOnce()
}

const handleLogin = async (main: MainState | undefined, vars: Vars, loginFailed: Boolean, oidc: OidcState | undefined) => { // can run mulitple times after odic relogin and app remount
  // if (!vars.oidc_client) {
  //   return handleFawkesLogin()
  // }
  if (!oidc) {
    return // check next time
  }
  if (loginFailed || main?.isLoggedIn) {
    return // handle by render
  }
  if (oidc.login && !oidc.oidcUser) {
    __info('clear session storage and login')
    clearOidcSessionStorage()
    return oidc.login()
  }
  if (!oidc.oidcUser) { // TODO: deal with this case later
    console.error('oidc failed to init')
    // const doc: any = window.document
    // if (doc._page_load_from_cache) {
    //   if (Date.now() - doc._page_load_from_cache > 10000) { // 10s
    //     doc._page_load_from_cache = undefined
    //   }
    //   else {
    //     const referrer = doc.referrer && new URL(doc.referrer)
    //     if (referrer && referrer.origin === window.location.origin) {
    //       window.location.reload()
    //     }
    //   }
    // }
    return
  }
  await handleOidcLogin(oidc)
}

export const getFawkesDefaultRedirect = (): string => {
  const { auth, main } = getState()
  if (hasTsgSupport() && hasMicroApp(ASC_PORTAL_MICRO_APP) && auth?.access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)) {
    return ASC_PATH_PREFIX // only have tier 1 role (ignore browser role), redirect to ASC
  }
  const isPanorama = main?.tenant?.type === PANORAMA_CONFIG || isPanoramaManagedTenant(auth)
  const isSaseIaView = auth?.instances?.flags?.redirectionView === RedirectionViews.SASE_IA && hasMicroApp('sase_ia')
  let redirect = MGMT_OVERVIEW_ROUTE
  if ((isPanorama && isUserRoleAdemSupport()) || auth?.access?.hasRole('adem_tier_1_support')) {
    redirect = isSaseIaView ? DASHBOARDS_PATH_PREFIX : '/dem/dem-summary'
  }
  else if ((isPanorama && !isMigrationStateEligibleForFawkesUI()) || isUserRoleHasManagePermission() === false) {
    redirect = isSaseIaView ? DASHBOARDS_PATH_PREFIX : '/insights/sase-summary'
  }
  else if (isUserRoleSaaSAdmin() && hasMicroApp(SAAS_MICRO_APP_NAME)) {
    redirect = '/manage/saas-security'
  }
  else if (hasMicroApp(DLP_MICRO_APP_NAME) && isUserRoleDlpOrDataSecurityAdmin()) {
    redirect = '/manage/prisma-access/data-loss-prevention'
  }
  return redirect
}

const dlpStandaloneTenant = (instances?: ReadonlyTsgInstances) => {
  return instances?.has(DLP_APP_ID) && !instances.has(NG_CASB_APP_ID) &&
    !instances.has(FAWKES_APP_ID) && !instances.has(SDWAN_APP_ID) &&
    !instances.has(AIOPS_APP_ID) && !instances.has(SAAS_APP_ID)
}
export const getDefaultRoute = () => { // it won't use getFawkesDefaultRedirect, instead use /prisma-access
  const auth = getAuthState()
  const vars = getFrameworkVars()
  const currentRoute = window.location.pathname
  if ((vars.aiops_free || vars.hub) && vars.default_route) {
    return vars.default_route
  }
  // 1. ASC with tier 1 role
  if (hasMicroApp(ASC_PORTAL_MICRO_APP) && auth.access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)) {
    return ASC_PATH_PREFIX
  }
  // 2. MSP view when tsg has parent or children
  if (auth.tsg_id && hasMicroApp(MSP_MICRO_APP_NAME) &&
    auth.tsgs?.hasSubTenants(auth.tsg_id) && // TODO: change to hasParentOrSubTenants after MSP implemented the new tsg selector
    (currentRoute === '/' || currentRoute.startsWith(MSP_PATH_PREFIX))) { // only redirect for / or in /msp
    // new logic for /msp, if the curernt tsg has parent or children
    return vars.msp_route || MSP_PATH_PREFIX
  }
  // 3. AIOps or ADEM Tier1 goes to /dashboards
  if (hasMicroApp(DASHBOARDS_MICRO_APP) &&
    (auth.instances?.isRunningStatus(AIOPS_APP_ID) || auth.access?.hasRole('adem_tier_1_support'))) {
    return DASHBOARDS_PATH_PREFIX
  }
  // 4. Fawkes cloud manage default redirect (not include panorama) /prisma-access
  if (auth.instances?.isFawkesRunning && !auth.instances?.isRunningStatus(PA_PANORAMA_APP_ID) && hasMicroApp(FAWKES_MICRO_APP_NAME)) {
    const fawkesDefaultRoute = vars.apps_can_launch?.[FAWKES_APP_ID]?.default_route || PA_DEFAULT_ROUTE
    return fawkesDefaultRoute
  }
  // 5. DLP standalone tenant /data-loss-prevention, no /dashboards
  if (hasMicroApp(DLP_MICRO_APP_NAME) && dlpStandaloneTenant(auth?.instances)) {
    return vars.apps_can_launch?.[DLP_APP_ID]?.default_route || DLP_DEFAULT_ROUTE
  }
  // then check for apps_can_launch
  if (vars.apps_can_launch && auth.instances) {
    for (const appId of Object.keys(vars.apps_can_launch)) {
      if (appId !== FAWKES_APP_ID &&
        auth.instances.isRunningStatus(appId) && isAppCanBeLaunched(auth.instances.get(appId))) {
        const appDefaultRoute = vars.apps_can_launch[appId].default_route
        if (appId === AIOPS_FREE_APP_ID && !vars.aiops_free) {
          continue
        }
        if (appDefaultRoute) {
          return appDefaultRoute
        }
        else if (appId === SDWAN_APP_ID) {
          return DASHBOARDS_PATH_PREFIX
        }
      }
    }
  }
  // last go to default route
  return vars.default_route
}

export default handleLogin
